import { getAccessToken } from '../authentication/supabaseService';
import { RevenueSource } from '../../types/RevenueSource';
import { RevenueSourceType } from '../../types/RevenueSourceType';

export interface BackendRestaurantRevenueSource {
  id: string;
  restaurant_id: string;
  revenue_source_type: RevenueSourceType;
  revenue_source_name: RevenueSource;
  restaurant_name: string;
  relevant_sections: string[];
  created_at: string;
  updated_at: string;
}

export interface FrontendRestaurantRevenueSource {
  id: string;
  restaurantId: string;
  revenueSourceType: RevenueSourceType;
  revenueSourceName: RevenueSource;
  restaurantName: string;
  relevantSections: string[];
  createdAt: string;
  updatedAt: string;
}

function mapBackendToFrontend(r: BackendRestaurantRevenueSource): FrontendRestaurantRevenueSource {
  return {
    id: r.id,
    restaurantId: r.restaurant_id,
    revenueSourceType: r.revenue_source_type,
    revenueSourceName: r.revenue_source_name,
    restaurantName: r.restaurant_name,
    relevantSections: r.relevant_sections,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

export async function createRestaurantRevenueSource(data: {
  restaurantId: string;
  revenueSourceType: RevenueSourceType;
  revenueSourceName: RevenueSource;
  restaurantName: string;
  relevantSections: string[];
}): Promise<FrontendRestaurantRevenueSource> {
  const token = await getAccessToken();
  const res = await fetch('/api/restaurantRevenueSources/create', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    let errorMessage = `Failed to create revenue source entry: ${res.status} ${res.statusText}`;
    try {
      const errorData = await res.json();
      if (errorData.error) {
        errorMessage = errorData.error;
      }
    } catch {
      // If we can't parse the error response, use the default message
    }
    throw new Error(errorMessage);
  }
  const backend: BackendRestaurantRevenueSource = await res.json();
  return mapBackendToFrontend(backend);
}

export async function fetchRestaurantSectionRevenueSources(
  restaurantSectionId: string
): Promise<FrontendRestaurantRevenueSource[]> {
  const token = await getAccessToken();
  const res = await fetch(`/api/restaurantRevenueSources/getAll/${restaurantSectionId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) {
    throw new Error(`Failed to fetch revenue source entries: ${res.status} ${res.statusText}`);
  }
  const list: BackendRestaurantRevenueSource[] = await res.json();
  return list.map(mapBackendToFrontend);
}

export async function fetchRevenueSourcesBySectionIds(
  sectionIds: string[]
): Promise<FrontendRestaurantRevenueSource[]> {
  const token = await getAccessToken();
  const res = await fetch('/api/restaurantRevenueSources/batch', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ sectionIds }),
  });
  if (!res.ok) {
    throw new Error(`Failed to fetch revenue source entries: ${res.status} ${res.statusText}`);
  }
  const list: BackendRestaurantRevenueSource[] = await res.json();
  return list.map(mapBackendToFrontend);
}

export async function fetchRevenueSourcesByRestaurantIds(
  restaurantIds: string[]
): Promise<FrontendRestaurantRevenueSource[]> {
  const token = await getAccessToken();
  const res = await fetch('/api/restaurantRevenueSources/batchByRestaurants', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ restaurantIds }),
  });
  if (!res.ok) {
    throw new Error(`Failed to fetch revenue source entries: ${res.status} ${res.statusText}`);
  }
  const list: BackendRestaurantRevenueSource[] = await res.json();
  return list.map(mapBackendToFrontend);
}

export async function updateRestaurantRevenueSource(
  id: string,
  data: {
    revenueSourceType: RevenueSourceType;
    revenueSourceName: RevenueSource;
    restaurantName: string;
    relevantSections: string[];
  }
): Promise<FrontendRestaurantRevenueSource> {
  const token = await getAccessToken();
  const res = await fetch(`/api/restaurantRevenueSources/${id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    let errorMessage = `Failed to update revenue source entry: ${res.status} ${res.statusText}`;
    try {
      const errorData = await res.json();
      if (errorData.error) {
        errorMessage = errorData.error;
      }
    } catch {
      // If we can't parse the error response, use the default message
    }
    throw new Error(errorMessage);
  }
  const backend: BackendRestaurantRevenueSource = await res.json();
  return mapBackendToFrontend(backend);
}

export async function deleteRestaurantRevenueSource(id: string): Promise<void> {
  const token = await getAccessToken();
  const res = await fetch(`/api/restaurantRevenueSources/delete/${id}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) {
    let errorMessage = `Failed to delete revenue source entry: ${res.status} ${res.statusText}`;
    try {
      const errorData = await res.json();
      if (errorData.error) {
        errorMessage = errorData.error;
      }
    } catch {
      // If we can't parse the error response, use the default message
    }
    throw new Error(errorMessage);
  }
}